import { Spinner } from "components/spinners/Spinner";
import React, { MouseEventHandler, useMemo } from "react";
import styles from "./index.module.scss";
import classnames from "classnames/bind";
import { CircularProgressLoader } from "components/spinners/CircularProgressLoader";
const cx = classnames.bind(styles);

export type ButtonVariant =
    | "primary"
    | "secondary"
    | "badge"
    | "danger"
    | "assignment"
    | "primary-outline"
    | "outline"
    | "action"
    | "text"
    | "icon"
    | "link"
    | "link-primary"
    | "box"
    | "badge-secondary"
    | "remove"
    | "badge-tertiary";

export interface SwitcherButtonProps {
    children: JSX.Element | JSX.Element[] | string | number;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    type?: ButtonVariant;
    disabled?: boolean;
    hide?: boolean;
    isSubmit?: boolean;
    isActive?: boolean;
    iconRight?: React.ReactNode;
    iconLeft?: React.ReactNode;
    value?: string;
    title?: string;
    tag?: "a" | "button";
    iconSize?: "small" | "medium";
    href?: string;
    rel?: string;
    target?: string;
    showSpinner?: boolean;
    showProgress?: boolean;
    progress?: number;
    noHover?: boolean;
    isInline?: boolean;
}

export const Button = ({
    children,
    onClick,
    type = "primary",
    disabled = false,
    hide = false,
    isSubmit = false,
    isActive = false,
    iconRight,
    iconLeft,
    value,
    title,
    tag = "button",
    iconSize = "medium",
    href,
    rel,
    target,
    showSpinner = false,
    showProgress = false,
    progress,
    noHover = false,
    isInline = false
}: SwitcherButtonProps) => {
    /** The bootstrap classes here should get taken out over time as we hone in on our own button styles */
    const buttonClass = useMemo(() => {
        switch (type) {
            case "primary":
                return "btn-primary";
            case "secondary":
                return "btn-secondary";
            case "danger":
                return "btn-danger";
            default:
                return styles[type];
        }
    }, [type]);

    const className = useMemo(() => {
        return `btn ${styles["button"]} ${buttonClass} ${styles[iconSize]} ${
            isActive ? styles["active"] : ""
        } ${showSpinner || showProgress ? styles["spinning"] : ""}`;
    }, [buttonClass, iconSize, isActive, showSpinner, showProgress]);

    const loader = useMemo(() => {
        if (showProgress) {
            return <CircularProgressLoader progress={progress} />;
        }
        if (showSpinner) {
            return <Spinner />;
        }
        return null;
    }, [progress, showProgress, showSpinner]);

    const contents = useMemo(() => {
        return (
            <>
                {loader ? (
                    loader
                ) : (
                    <>
                        {iconLeft && (
                            <div className={styles["icon-left"]}>
                                {iconLeft}
                            </div>
                        )}
                        <span>{children}</span>
                        {iconRight && (
                            <div className={styles["icon-right"]}>
                                {iconRight}
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }, [loader, iconLeft, children, iconRight]);

    if (hide) {
        return null;
    }

    return (
        <>
            {tag === "button" ? (
                <button
                    type={isSubmit ? "submit" : "button"}
                    onClick={onClick}
                    className={cx(className, { noHover, isInline })}
                    disabled={disabled}
                    value={value}
                    title={title}
                >
                    {contents}
                </button>
            ) : (
                <a
                    className={className}
                    href={href}
                    rel={rel}
                    title={title}
                    target={target}
                >
                    {contents}
                </a>
            )}
        </>
    );
};
